/**
 * Climate Training — Activation Engine
 * Route: /academy
 *
 * NOT a content library. An activation engine.
 * Every module ends with a platform action.
 *
 * Flow: Role Select → Track → Understand → Apply → Output → Certify
 *
 * Principle: Learn by doing. Certify by completing actions.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  Building2, BarChart3, Layers, Landmark, Star, ArrowRight,
  CheckCircle2, BookOpen, Zap, Award, ChevronDown, ChevronRight,
  Play, RefreshCw, Trophy, Target, Globe
} from "lucide-react";

const TRACK_ICONS = {
  building: Building2, chart: BarChart3, layers: Layers, bank: Landmark, star: Star,
};
const TRACK_COLORS = {
  sme:        { bg: "bg-teal-50",   border: "border-teal-200",   text: "text-teal-700",   dot: "bg-teal-500"   },
  corporate:  { bg: "bg-blue-50",   border: "border-blue-200",   text: "text-blue-700",   dot: "bg-blue-500"   },
  project:    { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", dot: "bg-purple-500" },
  fi:         { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-700",  dot: "bg-amber-500"  },
  individual: { bg: "bg-emerald-50",border: "border-emerald-200",text: "text-emerald-700",dot: "bg-emerald-500"},
};

// ── Institutional Mode Banner wrapper (supports auto-open) ─────────────────
function InstitutionalBannerWrapper({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  // Auto-scroll to form if coming from pricing
  useEffect(() => {
    if (defaultOpen && open) {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="institutional-banner"]');
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);
  // Reuse InstitutionalBanner but override showForm default
  return <InstitutionalBanner defaultShowForm={defaultOpen} />;
}

// ── Role Selection ────────────────────────────────────────────────────────
function RoleSelection({ tracks, onSelect, activeTrackId }) {
  const DESCRIPTIONS = {
    sme:        "Measure emissions, find opportunities, access financing.",
    corporate:  "Build portfolio, structure programs, deploy capital.",
    project:    "Define baselines, pass additionality, submit to FI.",
    fi:         "Screen deals, structure finance, manage portfolio.",
    individual: "Climate literacy, carbon markets, entrepreneurship.",
  };
  return (
    <div data-testid="role-selection">
      <div className="text-center mb-8">
        
      <PageHeader
        label="Certificate"
        title="Climate Training"
      />

      <MetricCard label="Learning Modules" value={modules.length} color="text-slate-900" />
        <p className="text-slate-400 text-sm">
          Learn by doing. Every module connects to a real platform action and generates a real output.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tracks.map(track => {
          const Icon = TRACK_ICONS[track.icon] || Building2;
          const c    = TRACK_COLORS[track.id] || TRACK_COLORS.sme;
          const isActive = activeTrackId === track.id;
          return (
            <button key={track.id}
              onClick={() => onSelect(track.id)}
              className={`text-left rounded-xl border-2 p-5 transition-all group ${
                isActive ? `${c.border} ${c.bg} shadow-sm` : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              data-testid={`select-track-${track.id}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${c.bg} border ${c.border}`}>
                <Icon size={18} className={c.text} />
              </div>
              <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${c.text}`}>{track.id.toUpperCase()}</p>
              <p className="text-sm font-semibold text-slate-900 mb-1.5">{track.label}</p>
              <p className="text-xs text-slate-400">{DESCRIPTIONS[track.id]}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-slate-400">{track.module_count} modules</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.bg} border ${c.border} ${c.text}`}>
                  {track.cert_label}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Module Card — 3-Layer Structure ───────────────────────────────────────
function ModuleCard({ module, index, isCompleted, onComplete, navigate }) {
  const [expanded, setExpanded] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    setApplying(true);
    await onComplete(module.id, "apply");
    setTimeout(() => {
      navigate(module.apply_path);
      setApplying(false);
    }, 300);
  };

  const handleMarkDone = async () => {
    await onComplete(module.id, "output");
    toast.success(`Module completed: ${module.title}`);
  };

  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all ${
      isCompleted ? "border-emerald-200 bg-white" : "border-slate-200 bg-white hover:border-slate-300"
    }`}
      data-testid={`module-${module.id}`}>
      {/* Module header */}
      <div className="flex items-start gap-4 px-5 py-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}>
        {/* Step number */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
          isCompleted ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"
        }`}>
          {isCompleted ? <CheckCircle2 size={14} /> : index + 1}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-900">{module.title}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {isCompleted
              ? "Completed"
              : (module.understand
                  ? module.understand.substring(0, 70) + (module.understand.length > 70 ? "..." : "")
                  : "Click to expand")}
          </p>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform flex-shrink-0 mt-1 ${expanded ? "rotate-180" : ""}`} />
      </div>

      {/* 3-layer content */}
      {expanded && (
        <div className="border-t border-slate-100 px-5 pb-5 space-y-4 pt-4">
          {/* Layer 1: Understand */}
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <BookOpen size={12} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-blue-600 font-bold uppercase tracking-wide mb-0.5">Understand</p>
              <p className="text-xs text-slate-600 leading-relaxed">{module.understand}</p>
            </div>
          </div>

          {/* Layer 2: Apply (action button) */}
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Zap size={12} className="text-teal-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-teal-600 font-bold uppercase tracking-wide mb-1.5">Apply</p>
              <button onClick={handleApply} disabled={applying}
                className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40 w-full sm:w-auto"
                data-testid={`apply-${module.id}`}>
                {applying ? <RefreshCw size={11} className="animate-spin" /> : <Play size={11} />}
                {applying ? "Opening..." : module.apply_label}
                {!applying && <ArrowRight size={11} />}
              </button>
            </div>
          </div>

          {/* Layer 3: Output */}
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Target size={12} className="text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-emerald-600 font-bold uppercase tracking-wide mb-1">Output</p>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-600">{module.output_label}</p>
                {!isCompleted && (
                  <button onClick={handleMarkDone}
                    className="text-xs font-bold text-emerald-600 hover:underline flex-shrink-0 ml-3"
                    data-testid={`mark-done-${module.id}`}>
                    Mark complete →
                  </button>
                )}
                {isCompleted && (
                  <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 flex-shrink-0">
                    <CheckCircle2 size={10} /> Done
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Certificate Display ───────────────────────────────────────────────────
function CertificateCard({ cert }) {
  return (
    <div className="bg-[#0F172A] rounded-xl p-6 text-white" data-testid="certificate-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-teal-600/20 border border-teal-400/30 rounded-xl flex items-center justify-center flex-shrink-0">
          <Trophy size={22} className="text-teal-400" />
        </div>
        <div>
          <p className="text-xs text-teal-400 font-bold uppercase tracking-widest mb-1">Certificate</p>
          <p className="text-lg font-bold text-white">{cert.cert_label}</p>
          <p className="text-xs text-slate-400 mt-0.5">{cert.user_name} · {cert.issued_date}</p>
          <p className="text-xs text-slate-500 mt-2 font-mono">{cert.cert_id}</p>
        </div>
      </div>
      <p className="text-xs text-slate-300 mt-4 leading-relaxed">{cert.statement}</p>
      <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
        <Globe size={10} />
        <span>{cert.issuer}</span>
      </div>
    </div>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────
function ProgressBar({ completed, total, certLabel }) {
  const pct = total > 0 ? Math.round(completed / total * 100) : 0;
  const isComplete = pct === 100;
  return (
    <SectionCard className="mb-5">

      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-xs font-bold text-slate-900">{certLabel}</p>
          <p className="text-xs text-slate-400">{completed}/{total} modules completed</p>
        </div>
        {isComplete && (
          <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
            <Award size={10} /> Certificate Ready
          </span>
        )}
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${isComplete ? "bg-emerald-500" : "bg-teal-500"}`}
          style={{ width: `${pct}%` }} />
      </div>
    
    </SectionCard>
  );
}

// ── Institutional Mode Banner + Intake Form ────────────────────────────────
function InstitutionalBanner({ defaultShowForm = false }) {
  const [showForm, setShowForm] = useState(defaultShowForm);
  const [busy,     setBusy]     = useState(false);
  const [submitted,setSubmitted]= useState(false);
  const [form, setForm] = useState({
    contact_name:  "", contact_email: "",
    organization:  "", org_type: "",
    use_cases:     [],
    users_estimate:"",
    message:       "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const ORG_TYPES = [
    { v: "financial_institution", l: "Bank / DFI / Fund"               },
    { v: "government",            l: "Government Agency"                },
    { v: "corporate",             l: "Corporate / Industrial Group"     },
    { v: "other",                 l: "NGO / Accelerator / University"   },
  ];
  const USE_CASES = [
    { v: "sme_training",          l: "Train SME clients or suppliers"   },
    { v: "corporate",             l: "Corporate decarbonization teams"  },
    { v: "project_development",   l: "Project developer pipeline"       },
    { v: "climate_finance",       l: "Climate finance teams"            },
  ];
  const USER_RANGES = [
    { v: "50-100",  l: "50–100 users"   },
    { v: "100-1000",l: "100–1,000 users"},
    { v: "1000+",   l: "1,000+ users"   },
  ];

  const toggleUseCase = (v) => {
    setForm(f => ({
      ...f,
      use_cases: f.use_cases.includes(v) ? f.use_cases.filter(u => u !== v) : [...f.use_cases, v],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_email || !form.organization || !form.org_type) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    setBusy(true);
    try {
      await client.post("/billing/enterprise-inquiry", {
        contact_name:  form.contact_name,
        contact_email: form.contact_email,
        organization:  form.organization,
        org_type:      form.org_type,
        use_cases:     form.use_cases,
        volume_range:  ">1000t",
        urgency:       "near_term",
        message:       `Training users: ${form.users_estimate}. ${form.message}`,
        role_title:    "Institutional Partner",
      });
      setSubmitted(true);
      toast.success("Application received — we'll be in touch within 48 hours.");
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail;
      if (typeof msg === "string" && msg.includes("work")) {
        toast.error("Please use a work or institutional email address.");
      } else {
        toast.error("Submission failed. Please try again.");
      }
    }
    setBusy(false);
  };

  return (
    <div className="mb-6">
      <div className={`bg-[#0F172A] rounded-xl p-5 text-white transition-all ${showForm ? "rounded-b-none" : ""}`}
        data-testid="institutional-banner">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs text-teal-400 font-bold uppercase tracking-widest mb-1">Institutional Mode</p>
            <p className="text-sm font-bold text-white mb-1">Train your teams, clients, and partners at scale</p>
            <p className="text-xs text-slate-400 mb-2">
              One organization → 100–10,000 users. Banks, governments, corporates, and NGOs.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {["Banks & DFIs", "Government", "Corporate groups", "NGOs"].map(t => (
                <span key={t} className="text-xs font-semibold text-teal-400 bg-teal-400/10 px-2 py-0.5 rounded-full border border-teal-400/20">{t}</span>
              ))}
            </div>
          </div>
          {!showForm && !submitted && (
            <button onClick={() => setShowForm(true)}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-500 whitespace-nowrap"
              data-testid="apply-institutional-btn">
              Apply for Access <ArrowRight size={11} />
            </button>
          )}
          {submitted && (
            <div className="flex-shrink-0 flex items-center gap-1.5 text-emerald-400 text-xs font-bold">
              <CheckCircle2 size={14} /> Received
            </div>
          )}
        </div>
      </div>

      {/* Intake form */}
      {showForm && !submitted && (
        <form onSubmit={handleSubmit}
          className="bg-slate-900 border-t border-white/8 rounded-b-2xl p-6 space-y-4"
          data-testid="institutional-intake-form">
          <h3 className="text-sm font-bold text-white">Institutional Access Application</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Your Name</label>
              <input value={form.contact_name} onChange={e => set("contact_name", e.target.value)}
                placeholder="e.g. Amara Okafor" required
                className="w-full h-10 px-3 bg-white/8 border border-white/15 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                data-testid="inst-contact-name" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Work Email *</label>
              <input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)}
                placeholder="amara@yourorg.com" required
                className="w-full h-10 px-3 bg-white/8 border border-white/15 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                data-testid="inst-email" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Organization Name *</label>
            <input value={form.organization} onChange={e => set("organization", e.target.value)}
              placeholder="e.g. Bank of Industry Nigeria" required
              className="w-full h-10 px-3 bg-white/8 border border-white/15 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
              data-testid="inst-org-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Organization Type *</label>
              <select value={form.org_type} onChange={e => set("org_type", e.target.value)} required
                className="w-full h-10 px-3 bg-white/8 border border-white/15 rounded-lg text-sm text-white focus:outline-none focus:border-teal-500"
                data-testid="inst-org-type">
                <option value="" className="text-slate-800">Select...</option>
                {ORG_TYPES.map(o => <option key={o.v} value={o.v} className="text-slate-800">{o.l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Estimated Users</label>
              <select value={form.users_estimate} onChange={e => set("users_estimate", e.target.value)}
                className="w-full h-10 px-3 bg-white/8 border border-white/15 rounded-lg text-sm text-white focus:outline-none focus:border-teal-500"
                data-testid="inst-users">
                <option value="" className="text-slate-800">Select range...</option>
                {USER_RANGES.map(r => <option key={r.v} value={r.v} className="text-slate-800">{r.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 uppercase tracking-wide font-semibold mb-2">Use Case (select all that apply)</label>
            <div className="flex flex-wrap gap-2">
              {USE_CASES.map(u => (
                <button key={u.v} type="button"
                  onClick={() => toggleUseCase(u.v)}
                  className={`text-xs px-3 py-1.5 rounded-xl border transition-all ${
                    form.use_cases.includes(u.v)
                      ? "bg-teal-600 border-teal-500 text-white"
                      : "bg-white/8 border-white/15 text-slate-400 hover:border-white/30"
                  }`}>
                  {u.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Additional context (optional)</label>
            <textarea value={form.message} onChange={e => set("message", e.target.value)}
              placeholder="Tell us about your program, timeline, or specific requirements..."
              rows={2}
              className="w-full px-3 py-2 bg-white/8 border border-white/15 rounded-lg text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-teal-500" />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={busy}
              className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
              data-testid="submit-institutional-btn">
              {busy ? <><RefreshCw size={12} className="animate-spin" /> Submitting...</> : <><ArrowRight size={12} /> Submit Application</>}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="text-xs text-slate-500 hover:text-slate-400">Cancel</button>
          </div>
          <p className="text-xs text-slate-600">We'll respond within 24–48 hours. Work email required.</p>
        </form>
      )}

      {submitted && (
        <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-b-2xl px-5 py-4 text-center"
          data-testid="institutional-success">
          <p className="text-sm font-bold text-emerald-400 mb-0.5">Application received</p>
          <p className="text-xs text-slate-400">We'll contact you within 24–48 hours to discuss your training program.</p>
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────
export default function Academy() {
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, activeRole } = useAuth();
  const [tracks,     setTracks]     = useState([]);
  const [activeTrack,setActiveTrack]= useState(null);
  const [completed,  setCompleted]  = useState({});
  const [certificate,setCertificate]= useState(null);
  const [loading,    setLoading]    = useState(true);
  // If ?mode=enterprise → open institutional form immediately
  const [showEntBanner, setShowEntBanner] = useState(searchParams.get("mode") === "enterprise");

  // Pre-select track based on user role
  const ROLE_TRACK_MAP = { sme:"sme", corporate:"corporate", project:"project", fi:"fi" };

  useEffect(() => {
    client.get("/training/tracks")
      .then(r => {
        const allTracks = r.data?.tracks || [];
        setTracks(allTracks);
        // Auto-select track based on user role — pre-filter to role track
        const defaultTrack = ROLE_TRACK_MAP[activeRole] || "sme";
        setActiveTrack(defaultTrack);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load saved progress from localStorage
    const saved = localStorage.getItem("training_progress");
    if (saved) {
      try { setCompleted(JSON.parse(saved)); } catch {}
    }
  }, [activeRole]);

  // Role-filtered tracks — SME only sees SME track; admins/training role see all
  const visibleTracks = (user?.user_roles?.includes("training") || user?.role === "admin")
    ? tracks
    : tracks.filter(t => {
        const roleTrack = ROLE_TRACK_MAP[activeRole] || "sme";
        return t.id === roleTrack;
      });

  const handleComplete = async (moduleId, action) => {
    const updated = { ...completed, [moduleId]: true };
    setCompleted(updated);
    localStorage.setItem("training_progress", JSON.stringify(updated));
    try { await client.post("/training/complete", { module_id: moduleId, track_id: activeTrack, action }); } catch {}
  };

  const handleRequestCert = async () => {
    try {
      const r = await client.get(`/training/certificate/${activeTrack}`);
      setCertificate(r.data);
      toast.success(`${r.data.cert_label} issued!`);
    } catch { toast.error("Could not generate certificate."); }
  };

  const currentTrack = tracks.find(t => t.id === activeTrack);
  const completedCount = currentTrack
    ? currentTrack.modules.filter(m => completed[m.id]).length
    : 0;
  const isTrackComplete = currentTrack && completedCount === currentTrack.modules.length;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="academy-page">
      {/* Institutional Banner — auto-expanded if coming from pricing page */}
      <InstitutionalBannerWrapper defaultOpen={showEntBanner} />

      {/* Role selection */}
      {!activeTrack ? (
        <RoleSelection tracks={visibleTracks} onSelect={setActiveTrack} activeTrackId={activeTrack} />
      ) : (
        <>
          {/* Role selector tabs — only show role-relevant tracks */}
          <div className="flex items-center gap-2 flex-wrap mb-6">
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mr-1">Track:</p>
            {visibleTracks.map(t => {
              const c = TRACK_COLORS[t.id] || TRACK_COLORS.sme;
              return (
                <button key={t.id} onClick={() => { setActiveTrack(t.id); setCertificate(null); }}
                  className={`text-xs font-bold px-3 py-1.5 rounded-xl border-2 transition-all ${
                    activeTrack === t.id ? `${c.border} ${c.bg} ${c.text}` : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid={`track-tab-${t.id}`}>
                  {t.label}
                </button>
              );
            })}
          </div>

          {currentTrack && (
            <div className="max-w-2xl">
              {/* Progress */}
              <ProgressBar
                completed={completedCount}
                total={currentTrack.modules.length}
                certLabel={currentTrack.cert_label}
              />

              {/* Certificate display */}
              {certificate && <CertificateCard cert={certificate} />}

              {/* Modules */}
              <div className="space-y-3 mb-6">
                {currentTrack.modules.map((mod, i) => (
                  <ModuleCard
                    key={mod.id}
                    module={mod}
                    index={i}
                    isCompleted={!!completed[mod.id]}
                    onComplete={handleComplete}
                    navigate={navigate}
                  />
                ))}
              </div>

              {/* Certification CTA */}
              {isTrackComplete && !certificate && (
                <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-5 text-center mb-5">
                  <Trophy size={28} className="text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-bold text-emerald-700 mb-1">All modules complete!</p>
                  <p className="text-xs text-emerald-600 mb-4">You've earned your {currentTrack.cert_label}.</p>
                  <button onClick={handleRequestCert}
                    className="flex items-center gap-2 mx-auto px-6 py-2.5 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700"
                    data-testid="get-certificate-btn">
                    <Award size={14} /> Get Certificate
                  </button>
                </div>
              )}

              {/* Progress action */}
              {!isTrackComplete && completedCount > 0 && (
                <div className="text-center text-xs text-slate-400">
                  {currentTrack.modules.length - completedCount} module{currentTrack.modules.length - completedCount !== 1 ? "s" : ""} remaining to earn {currentTrack.cert_label}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
